import React, { useState, useEffect } from "react";
import { Modal, Form, Row, Col, Steps, Space, message, Button } from "antd";
import Title from "antd/lib/typography/Title";
import days from "dayjs";
import useAuth from "../../../hooks/useAuth";
import eventService from "../../../services/eventService";
import PageHeader from "../../headers/PageHeader";
import { categories } from "./components/eventCategories";
import {
  CAUSAS_CON_DETALLE,
  LocationSelector,
  EventTypeSelector,
  EventDetails,
} from "./components/EventFormFields";
import { useUser } from "../../../context/UserContext";

const PRESET_SOURCES = [
  "Plataforma Cise",
  "Policía",
  "Vigilancia privada",
  "Operador de medios tecnológicos",
  "Informe IHR",
  "Usuario Metro",
  "Pasajero",
];

// Reconstruye la selección de causas (checkboxes) a partir de los campos
// planos que guarda el evento (type, risk, causes) para precargar el formulario.
const buildInitialSelectedItems = (visibleCategories, item) => {
  if (!item?.type) return [];

  const category = visibleCategories.find((c) => c.key === item.type);
  if (!category) return [];

  const causesBase = (item.causes || []).map((cause) => {
    const base = CAUSAS_CON_DETALLE.find(
      (b) => cause === b || cause.startsWith(`${b}-`)
    );
    return base || cause;
  });

  const hasNestedOptions = category.subItems?.[0]?.options !== undefined;
  const items = [];

  if (hasNestedOptions) {
    category.subItems.forEach((subItem) => {
      subItem.options.forEach((option) => {
        if (causesBase.includes(option.value)) {
          items.push({
            key: `${category.key}:${subItem.key}:${option.value}`,
            category: category.key,
            subItem: subItem.key,
            value: option.value,
            label: option.label,
            protocol: option.protocol,
          });
        }
      });
    });
  } else {
    category.subItems.forEach((option) => {
      if (causesBase.includes(option.value)) {
        items.push({
          key: `${category.key}:na:${option.value}`,
          category: category.key,
          subItem: "na",
          value: option.value,
          label: option.label,
          protocol: option.protocol,
        });
      }
    });
  }

  return items;
};

const extractTitleDetail = (item) => {
  for (const cause of item?.causes || []) {
    const base = CAUSAS_CON_DETALLE.find((b) => cause.startsWith(`${b}-`));
    if (base) return cause.slice(base.length + 1);
  }
  return undefined;
};

const ModalEditarDatosEvento = ({ open, setOpen, item, fetchData }) => {
  const [loadingButton, setLoadingButton] = useState(false);
  const [form] = Form.useForm();
  const { session, locations } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const user = useUser();

  const visibleCategories =
    session?.user?.team === "CISE"
      ? categories.filter((category) => category.key !== "otros")
      : categories.filter((category) => category.key === "otros");

  useEffect(() => {
    if (open && item) {
      form.resetFields();
      setSelectedItems(buildInitialSelectedItems(visibleCategories, item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const handleSubmit = (values) => {
    if (selectedItems.length === 0) {
      message.error("Selecciona al menos un tipo de evento antes de continuar.");
      return;
    }

    const distinctCategories = new Set(selectedItems.map((it) => it.category));
    const distinctSubItems = new Set(selectedItems.map((it) => it.subItem));
    if (distinctCategories.size > 1 || distinctSubItems.size > 1) {
      message.error(
        "Todas las causas seleccionadas deben pertenecer al mismo tipo y riesgo."
      );
      return;
    }

    const source =
      values?.source === "Otro" ? values?.sourceOtro : values?.source;

    const formattedDateTime = days(values.date).format("YYYY-MM-DDTHH:mm");
    const causes = selectedItems.map((it) =>
      CAUSAS_CON_DETALLE.includes(it.value) && values?.titleDetail
        ? `${it.value}-${values.titleDetail}`
        : it.value
    );

    const rawLocation = JSON.parse(values.Lugar);
    const location = {
      zone: rawLocation.zone,
      name: rawLocation.name,
      means: rawLocation.means,
      type: rawLocation.type,
      vehicle: rawLocation.vehicle,
      specific: rawLocation.specific,
      geolocation: rawLocation.geolocation,
    };

    const registro = {
      protocols: [...new Set(selectedItems.map((it) => it.protocol))],
      type: selectedItems[0].category,
      risk: selectedItems[0].subItem,
      causes,
      location,
      title: values.title,
      impact: item.impact || "leve",
      date: formattedDateTime + ":00-05:00",
      userId: item.createdBy || session?.user?.id || session?.user?.sub,
      source: source || "Plataforma Cise",
      team: item.team || session?.user?.team,
      intention: values.intention || item.intention || "",
    };

    setLoadingButton(true);
    ActualizarDatos(registro);
  };

  const ActualizarDatos = async (registro) => {
    try {
      await eventService.actualizarEventoInicial(
        item.id,
        registro,
        user?.idToken || ""
      );
      message.success("El evento se actualizó exitosamente.");
      if (fetchData) {
        fetchData();
      }
      setOpen(false);
    } catch (error) {
      console.error("Error al actualizar evento:", error);
      message.error("No se logró actualizar el evento.");
    }
    setLoadingButton(false);
  };

  const initialSource =
    item?.source && PRESET_SOURCES.includes(item.source)
      ? item.source
      : item?.source
      ? "Otro"
      : undefined;

  return (
    <Modal
      centered
      open={open}
      onCancel={() => setOpen(false)}
      width={"90%"}
      footer={false}
      destroyOnClose
    >
      <PageHeader title="Editar Evento" head="Eventos">
        <Title level={3}>Fecha: {days().format("YYYY/MM/DD HH:mm:ss")}</Title>
      </PageHeader>
      {item && (
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
          initialValues={{
            date: item.date ? days(item.date) : days(),
            title: item.title || "",
            source: initialSource,
            sourceOtro: initialSource === "Otro" ? item.source : undefined,
            titleDetail: extractTitleDetail(item),
            intention: item.intention || undefined,
          }}
        >
          <Steps
            current={0}
            items={[
              { title: "Selecciona la ubicación" },
              { title: "Selecciona uno o más tipos de evento" },
              { title: "Añadir descripción" },
            ]}
          />
          <Space />
          <Row gutter={16}>
            <Col span={8}>
              <LocationSelector
                form={form}
                locations={locations}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                initialLocation={item.location}
              />
            </Col>
            <Col span={8}>
              <EventTypeSelector
                categories={visibleCategories}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            </Col>
            <Col span={8}>
              <EventDetails selectedItems={selectedItems} showIntention />
            </Col>
          </Row>
          <Button
            htmlType="submit"
            style={{ marginTop: "20px" }}
            block
            type="primary"
            loading={loadingButton}
          >
            Guardar Cambios
          </Button>
        </Form>
      )}
    </Modal>
  );
};

export default ModalEditarDatosEvento;
